import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ApiRequestError } from '../api/client'
import { activateBuiltinSkill, listBuiltinSkills, readBuiltinSkill, setBuiltinSkillEnabled } from '../api/builtinSkills'
import type { SkillView } from '../generated/directorSkillTypes'
import { useAuth } from '../contexts/AuthContext'
import AdminToolNav from '../components/admin/AdminToolNav'
import Modal from '../components/ui/Modal'
import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('dw:admin-builtin-skills:release')
const button = 'h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50'
const primary = 'h-9 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50'

function SkillRow({ initial }: { initial: SkillView }) {
  const { t } = useTranslation('pages')
  const { user } = useAuth()
  const [selected, setSelected] = useState('')
  const [confirmation, setConfirmation] = useState<SkillView | null>(null)
  const [busy, setBusy] = useState(false)
  const running = useRef(false)
  const operationTrace = useRef<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const query = useQuery({
    queryKey: ['admin-builtin-skill', user?.id, initial.skill_id],
    queryFn: async () => {
      try {
        const data = await readBuiltinSkill(initial.skill_id)
        log({ event: 'admin-builtin-skills:row-refreshed', feature: 'admin-builtin-skills',
          skill_id: initial.skill_id, trace_id: operationTrace.current, enabled: data.skill.enabled,
          active_version_id: data.skill.is_active ? data.skill.version_id : null,
          active_version: data.skill.is_active ? data.skill.version : null,
          version_count: data.versions.length })
        return data
      } catch (cause) {
        log({ event: 'admin-builtin-skills:refresh-failed', feature: 'admin-builtin-skills', skill_id: initial.skill_id, trace_id: operationTrace.current,
          error_message: cause instanceof Error ? cause.message : String(cause),
          http_status: cause instanceof ApiRequestError ? cause.status : null })
        throw cause
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  })
  const skill = query.data?.skill
  const versions = query.data?.versions ?? []
  const name = skill?.display_name || initial.display_name || initial.name || initial.ref
  const currentId = skill?.is_active ? skill.version_id : ''
  const selectedId = selected || currentId || ''
  const target = versions.find((version) => version.version_id === selectedId)

  async function change(operation: 'activate' | 'enablement', version?: SkillView) {
    if (!skill || running.current) return
    if (operation === 'activate' && !version?.version_id) throw new Error('Activation requires a saved version.')
    running.current = true
    operationTrace.current = crypto.randomUUID()
    setBusy(true)
    setError(null)
    setNotice(null)
    const fields = { feature: 'admin-builtin-skills', trace_id: operationTrace.current, skill_id: skill.skill_id, operation,
      previous_enabled: skill.enabled, requested_enabled: operation === 'enablement' ? !skill.enabled : skill.enabled,
      previous_active_version_id: currentId || null, requested_version_id: version?.version_id ?? null }
    log({ event: 'admin-builtin-skills:change-requested', ...fields })
    let succeeded = false
    try {
      if (operation === 'activate' && version?.version_id) await activateBuiltinSkill(skill.skill_id, version.version_id)
      else await setBuiltinSkillEnabled(skill.skill_id, !skill.enabled)
      succeeded = true
      log({ event: 'admin-builtin-skills:change-succeeded', ...fields })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('adminSkills.changeError'))
      log({ event: 'admin-builtin-skills:change-failed', ...fields,
        error_message: cause instanceof Error ? cause.message : String(cause),
        http_status: cause instanceof ApiRequestError ? cause.status : null })
    } finally {
      setConfirmation(null)
      setSelected('')
      // Even a failed request may have reached the server. Read authoritative
      // state before accepting another operation; hide cached controls on error.
      try {
        await query.refetch({ throwOnError: true })
        if (succeeded) setNotice(t('adminSkills.saved'))
      } catch {
        setNotice(null)
      }
      operationTrace.current = null
      running.current = false
      setBusy(false)
    }
  }

  return (
    <section className="rounded-xl border border-border bg-surface-1 p-5" aria-label={name}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="break-words text-lg font-medium text-foreground">{name}</h2>
          <p className="mt-1 break-all text-xs text-text-muted">{initial.ref}</p>
        </div>
        <button className={button} disabled={busy || query.isFetching} onClick={() => { setNotice(null); void query.refetch() }} aria-label={t('adminSkills.refreshSkill', { name })}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
      {notice && <p role="status" className="mt-4 text-sm text-success">{notice}</p>}
      {query.isError ? (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {t('adminSkills.refreshError')} {query.error.message}
        </p>
      ) : !skill ? <p role="status" className="mt-4 text-sm text-muted-foreground">{t('adminSkills.loading')}</p> : (
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <p className="text-xs text-muted-foreground">{t('adminSkills.activeVersion')}</p>
            <p className="mt-1 text-base font-medium text-foreground" data-testid="active-version">
              {skill.is_active ? t('adminSkills.version', { version: skill.version }) : t('adminSkills.noActiveVersion')}
            </p>
            <button role="switch" aria-checked={skill.enabled} aria-label={t('adminSkills.availability', { name })}
              disabled={busy || query.isFetching} onClick={() => void change('enablement')}
              className={`${button} mt-4 flex items-center gap-3`}>
              <span aria-hidden="true" className={`flex h-5 w-9 items-center rounded-full px-0.5 ${skill.enabled ? 'bg-primary' : 'bg-surface-3'}`}>
                <span className={`h-4 w-4 rounded-full bg-foreground transition-transform ${skill.enabled ? 'translate-x-4' : ''}`} />
              </span>
              {skill.enabled ? t('adminSkills.enabled') : t('adminSkills.disabled')}
            </button>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {!skill.enabled ? t('adminSkills.disabledHint') : !skill.is_active ? t('adminSkills.draftHint') : t('adminSkills.enabledHint')}
            </p>
          </div>
          <div>
            <label htmlFor={`version-${initial.skill_id}`} className="text-xs text-muted-foreground">{t('adminSkills.savedVersions')}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              <select id={`version-${initial.skill_id}`} value={selectedId} disabled={busy || query.isFetching || versions.length === 0}
                onChange={(event) => setSelected(event.target.value)}
                className="h-9 min-w-0 flex-1 rounded-lg border border-input-border bg-input-bg px-3 text-base text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
                <option value="" disabled>{versions.length ? t('adminSkills.chooseVersion') : t('adminSkills.noVersions')}</option>
                {versions.map((version) => <option key={version.version_id} value={version.version_id ?? undefined}>
                  {t(version.version_id === currentId ? 'adminSkills.currentVersionOption' : 'adminSkills.version', { version: version.version })}
                </option>)}
              </select>
              <button className={primary} disabled={busy || query.isFetching || !target || selectedId === currentId} onClick={() => { if (target) setConfirmation(target) }}>
                {t('adminSkills.activate')}
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t('adminSkills.immutableHint')}</p>
          </div>
        </div>
      )}
      <Modal isOpen={confirmation !== null} onClose={() => { if (!busy) setConfirmation(null) }} title={t('adminSkills.confirmTitle')} size="sm">
        <p className="text-base leading-relaxed text-foreground">{t('adminSkills.confirmBody', { name, version: confirmation?.version })}</p>
        <p className="mt-3 text-sm text-muted-foreground">{t('adminSkills.confirmAvailability')}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button className={button} disabled={busy} onClick={() => setConfirmation(null)}>{t('adminSkills.cancel')}</button>
          <button className={primary} disabled={busy} onClick={() => { if (confirmation) void change('activate', confirmation) }}>{t('adminSkills.confirmActivate')}</button>
        </div>
      </Modal>
    </section>
  )
}

export default function AdminSkillsPage() {
  const { t } = useTranslation('pages')
  const { user } = useAuth()
  const query = useQuery({ queryKey: ['admin-builtin-skills', user?.id], queryFn: listBuiltinSkills, retry: false })
  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <AdminToolNav />
      <header className="mb-8 flex items-start justify-between gap-4 border-b border-border-subtle pb-8">
        <div>
          <BookOpen className="mb-3 h-5 w-5 text-primary" aria-hidden="true" />
          <h1 className="text-hero font-semibold tracking-tight text-foreground">{t('adminSkills.title')}</h1>
          <p className="mt-2 max-w-2xl text-base text-muted-foreground">{t('adminSkills.description')}</p>
        </div>
        <button className={button} disabled={query.isFetching} onClick={() => void query.refetch()}>{t('adminSkills.refreshList')}</button>
      </header>
      {query.isPending ? <p role="status">{t('adminSkills.loading')}</p> :
        query.isError ? <p role="alert" className="text-destructive">{query.error.message}</p> :
          query.data.length === 0 ? <p className="text-muted-foreground">{t('adminSkills.empty')}</p> :
            <div className="space-y-4">{query.data.map((skill) => <SkillRow key={skill.skill_id} initial={skill} />)}</div>}
    </div>
  )
}
